// VideoScene.jsx
import React, { Suspense, useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Canvas, useFrame, useLoader, useThree, extend } from '@react-three/fiber';
import { Center, shaderMaterial, useGLTF, useVideoTexture } from '@react-three/drei';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import * as THREE from 'three';
import gsap from 'gsap';
import './VideoScene.css';

// Custom hook for viewport detection
const useIsInViewport = (ref) => {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      {
        rootMargin: '100px',
        threshold: 0.1
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [ref]);

  return isIntersecting;
};

// Custom hook for scroll state
const useScrollState = () => {
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    let scrollTimeout;
    const handleScroll = () => {
      setIsScrolling(true);
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => setIsScrolling(false), 150);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  return isScrolling;
};

// Custom gradient material with glow effect
const GradientMaterial = shaderMaterial(
  {
    time: 0,
    colorA: new THREE.Color('#3B0563'),
    colorB: new THREE.Color('#602997'),
    glowColor: new THREE.Color('#ff69b4')
  },
  // Vertex shader
  `
    varying vec2 vUv;
    varying vec3 vNormal;
    void main() {
      vUv = uv;
      vNormal = normalize(normalMatrix * normal);
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  // Fragment shader with glow
  `
    uniform vec3 colorA;
    uniform vec3 colorB;
    uniform vec3 glowColor;
    uniform float time;
    varying vec2 vUv;
    varying vec3 vNormal;
    
    void main() {
      vec3 baseColor = mix(colorA, colorB, vUv.y);
      float pulse = (sin(time * 2.0) + 1.0) * 0.5;
      float glowIntensity = pow(1.0 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 3.0);
      vec3 glow = glowColor * glowIntensity * pulse;
      vec3 finalColor = baseColor + glow * 0.5;
      gl_FragColor = vec4(finalColor, 1.0);
    }
  `
);

extend({ GradientMaterial });

// Optimized Video Plane Component
const VideoPlane = React.memo(({ visible, isInViewport }) => {
  const meshRef = useRef();
  const playButtonRef = useRef();
  const [hasPlayed, setHasPlayed] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(true);

  const video = useMemo(() => {
    const vid = document.createElement('video');
    vid.src = '/basedstationvid.mp4';
    vid.crossOrigin = 'anonymous';
    vid.loop = false;
    vid.muted = false;
    vid.playsInline = true;
    vid.preload = 'auto';
    return vid;
  }, []);

  const texture = useMemo(() => {
    const tex = new THREE.VideoTexture(video);
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.format = THREE.RGBAFormat;
    return tex;
  }, [video]);

  useEffect(() => {
    if (!isInViewport) {
      video.pause();
    }
    
    if (visible && isInViewport) {
      const tl = gsap.timeline();

      if (meshRef.current) {
        tl.fromTo(meshRef.current.position,
          { z: -1500 },
          {
            z: -1.8,
            duration: 2,
            ease: "power2.out",
            delay: 2.5
          }
        );

        tl.fromTo(meshRef.current.rotation,
          { y: Math.PI * 0.1 },
          {
            y: 0,
            duration: 2,
            ease: "power2.out",
          },
          "<"
        );
      }

      if (playButtonRef.current) {
        tl.fromTo(playButtonRef.current.position,
          { z: -1500 },
          {
            z: -1.7,
            duration: 2,
            ease: "power2.out",
          },
          "<"
        );

        tl.fromTo(playButtonRef.current.rotation,
          { y: Math.PI * 0.1 },
          {
            y: 0,
            duration: 2,
            ease: "power2.out",
          },
          "<"
        );
      }
    }

    return () => {
      video.pause();
      video.currentTime = 0;
    };
  }, [visible, isInViewport, video]);

  const handleClick = useCallback(() => {
    if (!hasPlayed && video) {
      video.volume = 1;
      video.muted = false;
      video.play()
        .then(() => {
          setHasPlayed(true);
          setShowPlayButton(false);
        })
        .catch(error => {
          console.error("Video play failed:", error);
        });
    }
  }, [hasPlayed, video]);

  useEffect(() => {
    const onEnded = () => {
      setShowPlayButton(true);
      setHasPlayed(false);
    };
    video.addEventListener('ended', onEnded);
    return () => video.removeEventListener('ended', onEnded);
  }, [video]);

  const videoAspect = 13 / 10;
  const planeWidth = 11;
  const planeHeight = planeWidth / videoAspect;

  if (!visible || !isInViewport) return null;

  return (
    <group>
      <mesh 
        ref={meshRef} 
        position={[-1, -1, -1500]}
        rotation={[0, Math.PI * 0.1, 0]}
        onClick={handleClick}
      >
        <planeGeometry args={[planeWidth, planeHeight]} />
        <meshBasicMaterial 
          map={texture} 
          toneMapped={false}
        />
      </mesh>
      
      {showPlayButton && (
        <mesh
          ref={playButtonRef}
          position={[-1, -1, -1500]}
          rotation={[0, Math.PI * 0.1, 0]}
          onClick={handleClick}
        >
          <planeGeometry args={[2, 2]} />
          <meshBasicMaterial transparent opacity={0.8}>
            <canvasTexture
              attach="map"
              image={(() => {
                const canvas = document.createElement('canvas');
                canvas.width = 128;
                canvas.height = 128;
                const ctx = canvas.getContext('2d');
                ctx.fillStyle = 'white';
                ctx.beginPath();
                ctx.moveTo(30, 20);
                ctx.lineTo(100, 64);
                ctx.lineTo(30, 108);
                ctx.closePath();
                ctx.fill();
                return canvas;
              })()}
            />
          </meshBasicMaterial>
        </mesh>
      )}
    </group>
  );
});

// Optimized Particle Component
const ParticleCanvas = React.memo(({ isScrolling }) => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const animationFrameRef = useRef();
  const resizeTimeoutRef = useRef();

  class Particle {
    constructor(canvas) {
      this.canvas = canvas;
      this.reset();
    }

    reset() {
      this.x = Math.random() * this.canvas.width;
      this.y = this.canvas.height + 10;
      this.vx = (Math.random() - 0.5) * 1;
      this.vy = -Math.random() * 1.5 - 0.5;
      this.alpha = 0.8;
      this.size = Math.random() * 0.8 + 0.3;
    }

    update() {
      this.x += this.vx;
      this.y += this.vy;
      if (this.y < this.canvas.height * 0.05) {
        this.alpha -= 0.01;
      }
      if (this.y < -10 || this.x < -10 || this.x > this.canvas.width + 10 || this.alpha <= 0) {
        this.reset();
      }
    }

    draw(ctx) {
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(192, 132, 252, ${this.alpha})`;
      ctx.fill();
    }
  }

  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    
    resizeTimeoutRef.current = setTimeout(() => {
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }
    }, 100);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Reduce particle count while scrolling
    const particleCount = isScrolling ? 2 : 5;
    particlesRef.current = Array(particleCount).fill().map(() => new Particle(canvas));

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current.forEach(particle => {
        particle.update();
        particle.draw(ctx);
      });
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize, isScrolling]);

  return <canvas ref={canvasRef} className="particles-canvas" />;
});

// Optimized SimLogo Component
const SimLogo = React.memo(function SimLogo({ isHovered, setIsHovered, onLogoClick, isScrolling }) {
  const meshRef = useRef();
  const materialRef = useRef();
  const model = useLoader(STLLoader, '/3dsimlogo.stl');
  const { camera } = useThree();
  
  const rotationRef = useRef({
    x: 0,
    y: 0,
    z: 0
  });

  useEffect(() => {
    if (model) {
      model.center();
      model.computeBoundingBox();
      const box = new THREE.Box3().setFromObject(new THREE.Mesh(model));
      const size = box.getSize(new THREE.Vector3());
      const maxSize = Math.max(size.x, size.y, size.z);
      const scale = 6 / maxSize;
      model.scale(scale, scale, scale);
    }
  }, [model]);

  const handleClick = useCallback(() => {
    const tl = gsap.timeline({
      onStart: () => {
        onLogoClick();
      }
    });

    tl.to(meshRef.current.position, {
      x: 1,
      z: 5,
      duration: 1.5,
      ease: "power2.in"
    })
    .to(meshRef.current.scale, {
      x: 2,
      y: 2,
      z: 2,
      duration: 1.5,
      ease: "power2.in"
    }, "<")
    .to(meshRef.current.position, {
      x: 1,
      z: 600,
      y: 200,
      duration: 1,
      ease: "power2.in"
    })
    .to(meshRef.current.material, {
      opacity: 0,
      duration: 0.5,
      ease: "power2.in"
    }, "-=1");
  }, [onLogoClick]);

  useFrame((state) => {
    if (!isHovered && !isScrolling) {
      rotationRef.current.y += 0.002;
      rotationRef.current.x = Math.sin(state.clock.elapsedTime * 0.3) * 0.1;
      rotationRef.current.z = Math.cos(state.clock.elapsedTime * 0.2) * 0.05;
      
      meshRef.current.rotation.x = rotationRef.current.x;
      meshRef.current.rotation.y = rotationRef.current.y;
      meshRef.current.rotation.z = rotationRef.current.z;
    }
    
    if (materialRef.current) {
      materialRef.current.time = state.clock.elapsedTime;
    }
  });

  return (
    <group position={[0.2, 0, 0]}>
      <Center>
        <mesh 
          ref={meshRef}
          onPointerOver={() => setIsHovered(true)}
          onPointerOut={() => setIsHovered(false)}
          onClick={handleClick}
        >
          <primitive object={model} />
          <gradientMaterial 
            ref={materialRef} 
            attach="material"
            transparent
            opacity={1}
            side={THREE.DoubleSide}
            emissiveIntensity={2}
            metalness={0.8}
            roughness={0.2}
          />
        </mesh>
        <pointLight
          position={[0, 0, 2]}
          distance={5}
          intensity={2}
          color="#ff69b4"
        />
        <pointLight
          position={[0, 2, 0]}
          distance={5}
          intensity={1.5}
          color="#602997"
        />
        <pointLight
          position={[0, -2, 0]}
          distance={5}
          intensity={1.5}
          color="#602997"
        />
        <spotLight
          position={[0, 0, 8]}
          angle={0.5}
          penumbra={0.5}
          intensity={2}
          color="#ffffff"
          target={meshRef.current}
          castShadow
        />
      </Center>
    </group>
  );
});

// Optimized TV Component
const TV = React.memo(function TV({ visible, isInViewport }) {
  const { scene } = useGLTF('/old_tv/scene.gltf');
  const tvRef = useRef();
  
  useEffect(() => {
    scene.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        
        // Optimize materials
        if (child.material) {
          child.material.needsUpdate = false;
        }
      }
    });
  }, [scene]);

  useEffect(() => {
    if (visible && isInViewport && tvRef.current) {
      gsap.fromTo(tvRef.current.position, 
        { z: -1500 },
        { 
          z: -5,
          duration: 2,
          ease: "power2.out",
          delay: 2.501
        }
      );
      
      gsap.fromTo(tvRef.current.rotation,
        { y: Math.PI * 0.1 },
        {
          y: 0,
          duration: 2,
          ease: "power2.out",
          delay: 2.5
        }
      );
    }
  }, [visible, isInViewport]);

  if (!visible || !isInViewport) return null;

  return (
    <group>
      <primitive 
        ref={tvRef}
        object={scene} 
        scale={[900, 900, 900]} 
        position={[.4, -1, -400]}
        rotation={[0, Math.PI * 0, 0]}
      />
      <VideoPlane visible={visible} isInViewport={isInViewport} />
    </group>
  );
});

// Main VideoScene Component
const VideoScene = React.memo(() => {
  const sceneRef = useRef(null);
  const isInViewport = useIsInViewport(sceneRef);
  const isScrolling = useScrollState();
  const [isHovered, setIsHovered] = useState(false);
  const [showTV, setShowTV] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);

  const handleLogoClick = useCallback(() => {
    setShowTV(true);
    setShowOverlay(false);
  }, []);

  return (
    <div className="scene-container" ref={sceneRef}>
      {isInViewport && <ParticleCanvas isScrolling={isScrolling} />}
      <div className="canvas-wrapper">
        <Canvas 
          camera={{ position: [0, 0, 10], far: 2000 }}
          style={{ height: '100vh' }}
          frameloop={isInViewport ? 'always' : 'never'}
          dpr={Math.min(window.devicePixelRatio, 2)}
          performance={{ min: 0.5 }}
        >
          <Suspense fallback={null}>
            {isInViewport && (
              <>
                <ambientLight intensity={0.7} color="#ffffff" />
                <directionalLight position={[10, 10, 5]} intensity={1.5} color="#ffffff" />
                <directionalLight position={[-10, -10, -5]} intensity={1} color="#602997" />
                
                <SimLogo 
                  isHovered={isHovered} 
                  setIsHovered={setIsHovered}
                  onLogoClick={handleLogoClick}
                  isScrolling={isScrolling}
                />
                <TV visible={showTV} isInViewport={isInViewport} />
              </>
            )}
          </Suspense>
        </Canvas>
      </div>
      {isHovered && showOverlay && (
        <div className="overlay-text">314</div>
      )}
    </div>
  );
});

export default VideoScene;